import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import './IngredientChart.css';

const IngredientBreakdownChart = ({ ingredients }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Check if dark mode is enabled
  const [isDarkMode, setIsDarkMode] = React.useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  
  // Listen for theme changes
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Clean up previous chart instance
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (!ingredients || ingredients.length === 0) {
      return;
    }

    // Extract ingredient data
    const ingredientNames = ingredients.map(ingredient => ingredient.ingredient_name);
    const quantitiesPerCan = ingredients.map(ingredient => parseFloat(ingredient.quantity_per_can) || 0);
    
    // Generate colors based on ingredient quantities
    const generateColors = (values) => {
      // Create a blue gradient from light to dark based on values
      const max = Math.max(...values);
      const min = Math.min(...values);
      const range = max - min || 1;
      
      return values.map(value => {
        // Normalize the value between 0 and 1
        const normalized = (value - min) / range;
        
        // Create a blue gradient color
        const r = Math.round(59 + (1 - normalized) * 140);
        const g = Math.round(130 + (1 - normalized) * 70);
        const b = Math.round(246);
        
        return `rgba(${r}, ${g}, ${b}, 0.8)`;
      });
    };
    
    const backgroundColors = generateColors(quantitiesPerCan);
    const borderColors = backgroundColors.map(color => color.replace('0.8', '1'));
    
    // Set chart colors based on theme
    const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
    const textColor = isDarkMode ? '#f3f4f6' : '#111827';

    // Create chart
    const ctx = chartRef.current.getContext('2d');
    
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ingredientNames,
        datasets: [{
          label: 'Quantity per Can',
          data: quantitiesPerCan,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.9
        }]
      },
      options: {
        indexAxis: 'y', // Horizontal bar chart
        responsive: true,
        maintainAspectRatio: false,
        color: textColor,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            titleColor: isDarkMode ? '#f3f4f6' : '#111827',
            bodyColor: isDarkMode ? '#d1d5db' : '#374151',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderWidth: 1,
            padding: 12,
            cornerRadius: 6,
            boxPadding: 6,
            displayColors: true,
            callbacks: {
              title: function(context) {
                return context[0].label;
              },
              label: function(context) {
                const unit = ingredients[context.dataIndex].unit || '';
                return `Quantity: ${context.raw} ${unit}`;
              }
            }
          }
        },
        scales: {
          y: {
            grid: {
              display: false
            },
            ticks: {
              color: textColor,
              font: {
                size: 12
              }
            }
          },
          x: {
            beginAtZero: true,
            grid: {
              color: gridColor,
              lineWidth: 0.5
            },
            ticks: {
              color: textColor,
              font: {
                size: 12
              },
              callback: function(value) {
                if (value === 0) return '0';
                if (value < 1) return value.toFixed(2);
                return value.toFixed(1);
              }
            },
            title: {
              display: true,
              text: 'Quantity per Can',
              color: textColor,
              font: {
                size: 13,
                weight: '600'
              }
            }
          }
        }
      }
    });

    // Clean up on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [ingredients, isDarkMode]);

  return (
    <div className="ingredient-chart">
      <h4>Ingredient Usage</h4>
      <div className="ingredient-chart-container">
        <canvas ref={chartRef} height="200"></canvas>
      </div>
    </div>
  );
};

export default IngredientBreakdownChart;
